import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Phone, Settings } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import { useCallStore } from '@/stores/useCallStore'
import { useProfile } from '@/api/profile'

type Espace = 'telephone' | 'administration'
type Phase = 'entree' | 'visible' | 'sortie'

/** Le temps de lire où l'on arrive, pas plus. */
const DUREE_MS = 2000
/** Une aide pour apprendre qu'il y a deux espaces : après trois passages, c'est su. */
const PASSAGES_MAX = 3
const FONDU_MS = 180

/** /fr/dashboard → Téléphone, /fr/manager… → Administration, le reste : aucun espace. */
function espaceDe(pathname: string): Espace | null {
  const segment = pathname.split('/')[2]
  if (segment === 'dashboard') return 'telephone'
  if (segment === 'manager') return 'administration'
  return null
}

/**
 * Un court moment entre le Téléphone et l'Administration, juste le nom de
 * l'espace où l'on arrive : les deux se ressemblent, le passage ne se sentait pas.
 * Seulement sur un vrai changement d'espace (jamais à l'arrivée ni à la
 * connexion), les trois premières fois par utilisateur, jamais pendant un
 * appel, et sous la modale d'appel entrant. Un clic ou Échap le ferme.
 *
 * Transitions CSS et minuteurs, pas d'animation pilotée en JavaScript : le
 * retrait ne dépend pas de la fin d'un fondu (un onglet en arrière-plan gèle
 * ces animations, et un calque invisible bloquerait les clics).
 */
export function PassageEspace() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const darkMode = useUIStore((s) => s.darkMode)
  const { data: profile } = useProfile()
  const userId = profile?.id
  const espace = espaceDe(pathname)
  const precedent = useRef(espace)
  const [vers, setVers] = useState<Espace | null>(null)
  const [phase, setPhase] = useState<Phase>('entree')
  const minuteurs = useRef<ReturnType<typeof setTimeout>[]>([])

  const fermer = () => {
    minuteurs.current.forEach(clearTimeout)
    minuteurs.current = []
    setVers(null)
  }

  useEffect(() => {
    const avant = precedent.current
    precedent.current = espace
    if (!avant || !espace || avant === espace) return
    const { currentCall, showGlobalIncomingCall } = useCallStore.getState()
    if (currentCall || showGlobalIncomingCall) return
    if (userId == null) return
    const { passagesEspaceVus, compterPassageEspace } = useUIStore.getState()
    if ((passagesEspaceVus[userId] ?? 0) >= PASSAGES_MAX) return
    compterPassageEspace(userId)
    minuteurs.current.forEach(clearTimeout)
    setVers(espace)
    setPhase('entree')
    minuteurs.current = [
      setTimeout(() => setPhase('visible'), 20),
      setTimeout(() => setPhase('sortie'), DUREE_MS - FONDU_MS),
      setTimeout(() => setVers(null), DUREE_MS),
    ]
  }, [espace])

  useEffect(() => () => minuteurs.current.forEach(clearTimeout), [])

  useEffect(() => {
    if (!vers) return
    const auClavier = (e: KeyboardEvent) => e.key === 'Escape' && fermer()
    window.addEventListener('keydown', auClavier)
    return () => window.removeEventListener('keydown', auClavier)
  }, [vers])

  if (!vers) return null

  const Icone = vers === 'administration' ? Settings : Phone
  const titre = vers === 'administration'
    ? t('sidebar.configuration', 'Administration')
    : t('manager.myPhone', 'Téléphone')

  return (
    <div
      role="status"
      aria-live="polite"
      onClick={fermer}
      style={{ transitionDuration: `${FONDU_MS}ms` }}
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center px-6 transition-opacity ease-out motion-reduce:transition-none',
        phase === 'visible' ? 'opacity-100' : 'opacity-0',
        phase === 'sortie' && 'pointer-events-none',
        darkMode ? 'bg-slate-950/95' : 'bg-white/95',
      )}
    >
      <div
        className={cn(
          'max-w-sm text-center transition-transform duration-300 ease-out motion-reduce:transition-none',
          phase === 'entree' ? 'translate-y-2' : 'translate-y-0',
        )}
      >
        <div
          className={cn(
            'mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl ring-1',
            darkMode ? 'bg-indigo-500/10 ring-indigo-500/30' : 'bg-blue-50 ring-blue-200',
          )}
        >
          <Icone className={cn('h-6 w-6', darkMode ? 'text-indigo-300' : 'text-blue-600')} />
        </div>
        <h2 className={cn('text-2xl font-semibold tracking-tight', darkMode ? 'text-white' : 'text-slate-900')}>
          {titre}
        </h2>
        {/* Le temps qui reste, sans chiffre. */}
        <div className={cn('mx-auto mt-6 h-0.5 w-32 overflow-hidden rounded-full', darkMode ? 'bg-slate-800' : 'bg-slate-200')}>
          <div
            style={{ width: phase === 'entree' ? '0%' : '100%', transitionDuration: `${DUREE_MS - 20}ms` }}
            className={cn('h-full transition-[width] ease-linear', darkMode ? 'bg-indigo-400' : 'bg-blue-600')}
          />
        </div>
      </div>
    </div>
  )
}
